'use client';

import { notFound } from 'next/navigation';
import { useState, useCallback } from 'react';
import { LocaleLink } from '@/core/i18n';
import { ArrowRight, ChevronDown, CheckCircle2, Zap } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { TEMPLATE_CHROME } from '@/core/i18n/content/template-chrome.content';
import type { TemplateChromeContent } from '@/core/i18n/content/template-chrome.types';
import { INTEGRATION_CONTENT } from '@/core/i18n/content/integrations';
import type { IntegrationLandingContent } from '@/core/i18n/content/integrations';

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

export interface IntegrationConfig {
  slug: string;
  partnerName: string;
  partnerType: string;
  heroTitle: string;
  heroSubtitle: string;
  benefits: { title: string; description: string }[];
  howItWorks: { step: number; title: string; description: string }[];
  features: { title: string; description: string }[];
  faqItems: { question: string; answer: string }[];
}

/* -------------------------------------------------------------------------- */
/*  Section — Hero                                                            */
/* -------------------------------------------------------------------------- */

function IntegrationHero({ config, t }: { config: IntegrationConfig; t: TemplateChromeContent }) {
  return (
    <section id={`integration-${config.slug}-hero`} className="relative w-full overflow-hidden bg-hms-bg">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <span className="mb-4 inline-block rounded-full border border-hms-gold/20 bg-hms-gold/5 px-4 py-1.5 text-xs font-semibold tracking-wide text-hms-gold">
          {config.partnerType}
        </span>
        <h1
          id={`integration-${config.slug}-title`}
          className={cn(
            'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {config.heroTitle}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">
          {config.heroSubtitle}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`integration-${config.slug}-cta-trial`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`integration-${config.slug}-cta-demo`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.bookDemo}
          </LocaleLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Benefits                                                        */
/* -------------------------------------------------------------------------- */

function IntegrationBenefits({ config, t }: { config: IntegrationConfig; t: TemplateChromeContent }) {
  return (
    <section id={`integration-${config.slug}-benefits`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.seoTemplate.whyConnectWith.replace('{0}', config.partnerName)}
        </h2>
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {config.benefits.map((benefit, idx) => (
            <div
              key={benefit.title}
              id={`integration-${config.slug}-benefit-${idx}`}
              className={cn(
                'group rounded-xl border border-hms-border bg-hms-card p-6',
                'transition-all duration-300 hover:border-hms-gold/20 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
              )}
            >
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-hms-gold/10 group-hover:bg-hms-gold/15 transition-colors">
                <CheckCircle2 className="h-5 w-5 text-hms-gold" />
              </div>
              <h3 className="mb-2 text-base font-bold text-hms-text tracking-tight">{benefit.title}</h3>
              <p className="text-sm leading-relaxed text-hms-textSub">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — How It Works                                                    */
/* -------------------------------------------------------------------------- */

function IntegrationHowItWorks({ config, t }: { config: IntegrationConfig; t: TemplateChromeContent }) {
  return (
    <section id={`integration-${config.slug}-how-it-works`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(200,145,58,0.04) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.common.howItWorks}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-center text-base text-hms-textSub">
          {t.seoTemplate.connectedSteps}
        </p>
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {config.howItWorks.map((step, idx) => (
            <div
              key={step.step}
              id={`integration-${config.slug}-step-${idx}`}
              className={cn(
                'group relative rounded-xl border border-hms-border bg-hms-card p-6',
                'transition-all duration-300 hover:border-hms-gold/20 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
              )}
            >
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-hms-gold/10 group-hover:bg-hms-gold/20 transition-colors">
                <span className="text-sm font-bold text-hms-gold">{step.step}</span>
              </div>
              <h3 className="mb-2 text-base font-bold text-hms-text tracking-tight">{step.title}</h3>
              <p className="text-sm leading-relaxed text-hms-textSub">{step.description}</p>
              {idx < config.howItWorks.length - 1 && (
                <div className="pointer-events-none absolute -right-3 top-1/2 -translate-y-1/2 hidden lg:block">
                  <ArrowRight className="h-5 w-5 text-hms-textDim" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Features                                                        */
/* -------------------------------------------------------------------------- */

function IntegrationFeatures({ config, t }: { config: IntegrationConfig; t: TemplateChromeContent }) {
  return (
    <section id={`integration-${config.slug}-features`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.common.integrationFeatures}
        </h2>
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {config.features.map((feat, idx) => (
            <div
              key={feat.title}
              id={`integration-${config.slug}-feature-${idx}`}
              className={cn(
                'group rounded-xl border border-hms-border bg-hms-card p-6',
                'transition-all duration-300 hover:border-hms-gold/20 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
              )}
            >
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-hms-gold/10 group-hover:bg-hms-gold/15 transition-colors">
                <Zap className="h-5 w-5 text-hms-gold" />
              </div>
              <h3 className="mb-2 text-base font-bold text-hms-text tracking-tight">{feat.title}</h3>
              <p className="text-sm leading-relaxed text-hms-textSub">{feat.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — FAQ                                                             */
/* -------------------------------------------------------------------------- */

function IntegrationFAQ({ config, t }: { config: IntegrationConfig; t: TemplateChromeContent }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggle = useCallback((i: number) => setOpenIndex((p) => (p === i ? null : i)), []);

  return (
    <section id={`integration-${config.slug}-faq`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.common.faq}
        </h2>
        <div className="mt-12 space-y-3">
          {config.faqItems.map((item, idx) => (
            <div
              key={idx}
              id={`integration-${config.slug}-faq-item-${idx}`}
              className={cn(
                'rounded-xl border transition-colors',
                openIndex === idx ? 'border-hms-gold/30 bg-hms-card' : 'border-hms-border bg-hms-card/50 hover:border-hms-gold/15',
              )}
            >
              <button
                id={`integration-${config.slug}-faq-toggle-${idx}`}
                type="button"
                onClick={() => toggle(idx)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={openIndex === idx}
              >
                <span className={cn('text-sm font-semibold leading-snug', openIndex === idx ? 'text-hms-gold' : 'text-hms-text')}>
                  {item.question}
                </span>
                <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform duration-200', openIndex === idx ? 'rotate-180 text-hms-gold' : 'text-hms-textDim')} />
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-5">
                  <p className="text-sm leading-relaxed text-hms-textSub">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — CTA                                                             */
/* -------------------------------------------------------------------------- */

function IntegrationCTA({ config, t }: { config: IntegrationConfig; t: TemplateChromeContent }) {
  return (
    <section id={`integration-${config.slug}-cta-final`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2
          className={cn(
            'text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.seoTemplate.readyToConnect.replace('{0}', config.partnerName)}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">
          {t.seoTemplate.startTrialActivate.replace('{0}', config.partnerName)}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`integration-${config.slug}-cta-final-trial`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`integration-${config.slug}-cta-final-contact`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.contactBtn}
          </LocaleLink>
        </div>
        <p className="mt-5 text-xs text-hms-textDim">{t.common.noCreditCard}</p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

export function IntegrationLanding({ slug }: { slug: string }) {
  const t = usePageContent(TEMPLATE_CHROME);

  // A slug with no content record renders 404, not a crash.
  //
  // The `!` here asserted the lookup always succeeds. It does not:
  // app/(public)/integrations has 17 pages and INTEGRATION_CONTENT has 16
  // entries, so /integrations/razorpay reached `record.content` on undefined:
  //
  //   TypeError: Cannot read properties of undefined (reading 'content')
  //
  // Measured against the production build: every localized variant returned
  // 500 (/ar, /de, /es, /fr all reproduce; /ar/integrations/stripe is 200, so
  // it is the missing record and not the locale machinery). A non-null
  // assertion cannot make a missing key present — it only removes the warning
  // that would have caught this.
  //
  // 404 is the honest answer for a page we have no content for, and it stops
  // one missing record from taking the route down for every locale.
  const record = INTEGRATION_CONTENT[slug];
  if (!record) notFound();

  const localized = usePageContent(record.content);

  const config: IntegrationConfig = {
    slug: record.constants.slug,
    partnerName: record.constants.partnerName,
    partnerType: localized.partnerType,
    heroTitle: localized.heroTitle,
    heroSubtitle: localized.heroSubtitle,
    benefits: localized.benefits,
    howItWorks: localized.howItWorks,
    features: localized.features,
    faqItems: localized.faqItems,
  };

  return (
    <main id={`integration-${config.slug}-page`}>
      <IntegrationHero config={config} t={t} />
      <IntegrationBenefits config={config} t={t} />
      <IntegrationHowItWorks config={config} t={t} />
      <IntegrationFeatures config={config} t={t} />
      <IntegrationFAQ config={config} t={t} />
      <IntegrationCTA config={config} t={t} />
    </main>
  );
}
